import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { availableDocuments } from '../../utils/documentAvailability';

/**
 * Menú de descarga de los documentos de una atención.
 *
 * Qué ofrecer se decide en el cliente, a partir de la historia ya cargada, con
 * las mismas condiciones que documentRegistry.availableKinds() del backend (ver
 * utils/documentAvailability.js, que la comparte con una prueba del backend).
 * Si divergen, el usuario ve una opción que termina en 404.
 *
 * La lista se dibuja en un portal con posición fija: se usa dentro de una tabla
 * con overflow y de modales con scroll, donde un menú absoluto quedaría recortado.
 *
 * No importa íconos de lucide a propósito: lo usan pantallas cuyas pruebas
 * mockean lucide-react con sus propios íconos.
 */
const MENU_WIDTH = 224;
const ROW_HEIGHT = 40;

function DocumentDownloadMenu({
  record,
  onDownload,
  busy = false,
  disabled = false,
  label = 'Descargar PDF',
  busyLabel = 'Descargando...',
  buttonClassName = 'px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed',
  className = 'relative inline-block',
}) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const buttonRef = useRef(null);
  const documents = availableDocuments(record);

  // Un menú fijo no acompaña al botón si la página se desplaza: se cierra.
  useEffect(() => {
    if (!open) return undefined;
    const close = () => setOpen(false);
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    window.addEventListener('scroll', close, true);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const toggle = () => {
    if (open) { setOpen(false); return; }
    const rect = buttonRef.current.getBoundingClientRect();
    const height = documents.length * ROW_HEIGHT + 8;
    // Si no cabe debajo (botones al pie de un modal) se abre hacia arriba.
    const opensUp = rect.bottom + height > window.innerHeight && rect.top > height;
    setPosition({
      top: opensUp ? rect.top - height - 4 : rect.bottom + 4,
      left: Math.max(8, rect.right - MENU_WIDTH),
    });
    setOpen(true);
  };

  return (
    <div className={className}>
      <button
        ref={buttonRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        disabled={busy || disabled}
        onClick={toggle}
        className={buttonClassName}
      >
        {busy ? busyLabel : label}
      </button>

      {open && !busy && createPortal(
        <>
          {/* Fondo transparente: un clic fuera del menú lo cierra. */}
          <div className="fixed inset-0" style={{ zIndex: 1000 }} onClick={() => setOpen(false)} />
          <div
            role="menu"
            className="fixed bg-white text-gray-800 border rounded-lg shadow-lg overflow-hidden"
            style={{ zIndex: 1001, top: position.top, left: position.left, width: MENU_WIDTH }}
          >
            {documents.map((doc) => (
              <button
                key={doc.kind}
                type="button"
                role="menuitem"
                className="block w-full text-left px-4 py-2 hover:bg-gray-50"
                onClick={() => { setOpen(false); onDownload(doc.kind); }}
              >
                {doc.label}
              </button>
            ))}
          </div>
        </>,
        document.body
      )}
    </div>
  );
}

export default DocumentDownloadMenu;
